import { useState, useEffect } from "react";
import { ChevronUp, ChevronDown, X } from "lucide-react";
import BlogLeadForm from "@/components/BlogLeadForm";

/**
 * Sticky bottom drawer that nudges blog readers into the lead funnel.
 * - Auto-opens after 8s of reading or when user scrolls past 60% of page.
 * - Once submitted or dismissed, stays out of the way.
 */
export default function StickyLeadDrawer({ slug, topicKey, category, accent = "#FF3B30" }) {
    const [open, setOpen] = useState(false);
    const [dismissed, setDismissed] = useState(false);
    const [submitted, setSubmitted] = useState(false);

    useEffect(() => {
        if (dismissed || submitted) return;
        const t = setTimeout(() => setOpen(true), 8000);
        const onScroll = () => {
            const scrolled = window.scrollY + window.innerHeight;
            const total = document.body.scrollHeight;
            if (total > 0 && scrolled / total > 0.6) setOpen(true);
        };
        window.addEventListener("scroll", onScroll, { passive: true });
        return () => {
            clearTimeout(t);
            window.removeEventListener("scroll", onScroll);
        };
    }, [dismissed, submitted]);

    if (dismissed || submitted) return null;

    return (
        <div
            className="fixed bottom-0 left-0 right-0 z-40 border-t-2 border-white/30 bg-[#0a0a0a]/95 backdrop-blur-md"
            data-testid="sticky-lead-drawer"
        >
            <div className="max-w-[900px] mx-auto px-4 sm:px-6">
                {!open ? (
                    <button
                        onClick={() => setOpen(true)}
                        className="w-full flex items-center justify-between py-3 text-left"
                        data-testid="drawer-open"
                    >
                        <span className="font-mono text-[10px] sm:text-xs uppercase tracking-[0.3em] text-white/80">
                            <span style={{ color: accent }}>✦</span> NEED A LAWYER WHO HANDLES{" "}
                            <span className="text-white">{(category || "this").toUpperCase()}</span>
                            ? FREE TO YOU.
                        </span>
                        <ChevronUp size={16} className="text-white/60" />
                    </button>
                ) : (
                    <div className="py-4">
                        <div className="flex items-center justify-between mb-3">
                            <span
                                className="font-mono text-[10px] uppercase tracking-[0.3em]"
                                style={{ color: accent }}
                            >
                                ✦ FREE LEGAL HELP — VETTED ATTORNEY · 24-HR RESPONSE
                            </span>
                            <div className="flex items-center gap-2">
                                <button
                                    onClick={() => setOpen(false)}
                                    className="text-white/50 hover:text-white"
                                    data-testid="drawer-collapse"
                                    aria-label="Collapse"
                                >
                                    <ChevronDown size={16} />
                                </button>
                                <button
                                    onClick={() => setDismissed(true)}
                                    className="text-white/50 hover:text-white"
                                    data-testid="drawer-dismiss"
                                    aria-label="Dismiss"
                                >
                                    <X size={16} />
                                </button>
                            </div>
                        </div>
                        <BlogLeadForm
                            slug={slug}
                            topicKey={topicKey}
                            category={category}
                            variant="drawer"
                            slot="drawer"
                            accent={accent}
                            onSubmitted={() => {
                                // Show success state for 2.5s so the user clearly
                                // sees it, then collapse the drawer.
                                setTimeout(() => setSubmitted(true), 2500);
                            }}
                        />
                    </div>
                )}
            </div>
        </div>
    );
}
